import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';

import { TableView, TableViewRow, useTableViewState } from '~components/table-view';

interface EditableTableModalProps<T extends TableViewRow> {
  isOpen: boolean;
  title: string;
  data: T[];
  columns: ColumnDef<T>[];
  onClose: (shouldReload: boolean) => void;
  onCreate: (row: T) => Promise<void>;
  onUpdate?: (row: T) => Promise<void>;
  onDelete?: (row: T) => Promise<void>;
  getDefaultRow: () => T;
  allowEdit?: boolean;
  allowAdd?: boolean;
  allowDelete?: boolean;
  validateRow?: (row: T) => string | null;
  /**
   * Optional: Called after a cell change to recalculate dependent/computed fields (e.g., max usage, contrib_proportion).
   * Receives the updated row, columnId, and value. Should return the updated row with computed fields.
   */
  onDependentChange?: (row: T, columnId: string, value: unknown) => T;
}

function EditableTableModal<T extends TableViewRow>({
  isOpen,
  title,
  data: initialData,
  columns,
  onClose,
  onCreate,
  onUpdate,
  getDefaultRow,
  onDelete,
  allowEdit = true,
  allowAdd = true,
  allowDelete = false,
  validateRow,
  onDependentChange,
}: EditableTableModalProps<T>) {
  const { data, setData, patchRow, updateNewRow } = useTableViewState<T>(initialData);
  const [newRow, setNewRow] = useState<T>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const combinedData = useMemo(() => {
    return [...data, newRow].filter(Boolean) as T[];
  }, [data, newRow]);

  useEffect(() => {
    setData(initialData);
  }, [initialData, setData]);

  const handleRowAdded = (row: TableViewRow) => {
    setNewRow(row as T);
    setValidationError(null);
  };

  const handleCancelEdit = (rollback?: TableViewRow) => {
    if (!rollback) return;
    const isNew = newRow?.id === rollback.id;
    if (isNew) {
      setNewRow(undefined);
    } else {
      setData((prev: T[]) => prev.map((r: T) => (r.id === rollback.id ? (rollback as T) : r)));
    }
    setValidationError(null);
  };

  const handleRowSaved = async (savedRow: TableViewRow) => {
    setValidationError(null);
    if (validateRow) {
      const validationMsg = validateRow(savedRow as T);
      if (validationMsg) {
        setValidationError(validationMsg);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      if (!savedRow.backendId && !savedRow.persisted) {
        await onCreate(savedRow as T);
      } else if (onUpdate) {
        await onUpdate(savedRow as T);
      }
      const updated = combinedData.map((r: T) => (r.id === savedRow.id ? (savedRow as T) : r));
      setData(updated);
      setNewRow(undefined);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to save row');
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (rowId?: string | number, columnId?: string, value?: unknown) => {
    if (!rowId || !columnId) return;
    const isNew = newRow?.id === rowId;
    let updatedRow: T | undefined;
    if (isNew && newRow) {
      updatedRow = { ...newRow, [columnId]: value };
    } else {
      const existingRow = data.find((row: T) => row.id === rowId);
      if (!existingRow) return;
      updatedRow = { ...existingRow, [columnId]: value };
    }
    if (!updatedRow) return;

    // If a dependent/computed field recalculation function is provided, use it
    if (onDependentChange) {
      updatedRow = onDependentChange(updatedRow, columnId, value);
    }

    if (isNew) {
      setNewRow(updatedRow);
      updateNewRow(updatedRow.id, columnId, value);
    }
    setData((prev: T[]) => prev.map((row: T) => (row.id === updatedRow?.id ? updatedRow : row)));
    patchRow(updatedRow);
    setValidationError(null);
  };

  const handleRowDeleted = (deleteRow: TableViewRow) => {
    if (!allowDelete) return;
    const updated = combinedData.filter((r: T) => r.id !== deleteRow.id);
    if (onDelete) {
      onDelete(deleteRow as T);
    }
    setData(updated);
    setNewRow(undefined);
    setValidationError(null);
  };

  return (
    <Modal isOpen={isOpen} size="xl" style={{ maxWidth: '1500px', width: '100%' }}>
      <ModalHeader className="bg-success text-white">{title}</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" data-cy="editable-table-modal-error">
            {error}
          </Alert>
        )}
        {validationError && (
          <Alert color="warning" data-cy="editable-table-modal-validation">
            {validationError}
          </Alert>
        )}
        {loading && (
          <div className="d-flex justify-content-center my-2">
            <Spinner color="primary" />
          </div>
        )}
        <TableView
          id="editable-table-modal"
          columns={columns}
          data={combinedData}
          allowSort={true}
          getRowId={(row: TableViewRow) => row.id as string}
          getRowById={(id: string | number) => combinedData.find((row: T) => row.id === id)}
          getDefaultRow={getDefaultRow}
          actions={{
            canAdd: allowAdd,
            canEdit: allowEdit,
            canDelete: allowDelete,
            onAddRow: handleRowAdded,
            onUpdateRow: handleRowSaved,
            onCancelEdit: handleCancelEdit,
            onDeleteRow: handleRowDeleted,
            onCellChange: handleCellChange,
          }}
          emptyMessage={'No data found'}
        />
      </ModalBody>
      <ModalFooter className="align-items-center flex-column">
        <div>
          <button type="button" className="btn btn-secondary" onClick={() => onClose(true)} disabled={loading}>
            Close
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default EditableTableModal;
